import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaSpinner } from "../utils/icons";
import { authService } from "../services/auth";
import { Alert, AlertDescription, Input, Label, Button } from "@/design-system/components/react";
import AuthLayout from "./AuthLayout";

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await authService.requestPasswordReset(email);
      // Always show the neutral confirmation — never reveal whether the account exists.
      setSubmitted(true);
    } catch {
      // Network / server errors surface here; still show the neutral message so
      // the response is indistinguishable whether the email is registered or not.
      setSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout eyebrow="Reset password" heading="Forgot your password?">
      {error && (
        <Alert variant="destructive" className="mb-5 p-3 bg-warn-tint">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {submitted ? (
        <div className="space-y-4">
          <Alert variant="warning" className="p-3">
            <AlertDescription>
              If an account exists for that email, a reset link has been sent.
            </AlertDescription>
          </Alert>
          <p className="text-center font-mono text-[9px] tracking-[0.05em] text-fg-faint">
            <Link to="/login" className="hover:text-fg-muted transition-colors">
              Back to sign in
            </Link>
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-fg-muted">
              Email address
            </Label>
            <Input
              id="email"
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>

          <Button type="submit" disabled={isLoading} className="w-full">
            {isLoading ? (
              <>
                <FaSpinner className="animate-spin mr-2 h-4 w-4" />
                <span>Sending...</span>
              </>
            ) : (
              <span>Send reset link</span>
            )}
          </Button>

          <p className="mt-4 text-center font-mono text-[9px] tracking-[0.05em] text-fg-faint">
            <Link to="/login" className="hover:text-fg-muted transition-colors">
              Back to sign in
            </Link>
          </p>
        </form>
      )}
    </AuthLayout>
  );
};

export default ForgotPassword;
